import { Loader, Search } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Input } from "../../components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs"
import placeholder from '../../util/placeholder';
import RadioGroup from "../../components/RadioGroup"
import { useCallback, useMemo, useState } from "react"
import DataView from "../../components/DataView/RentalDataView"
import useSWR from "swr"
import { fetcher } from "../../util/axios"
import { useCurrency } from "../../hooks/useCurrency";
import dayjs from "dayjs";

export default function RentalRecords() {
  const navigate = useNavigate();
  const [reqPayload, setReqPayload] = useState({
    status: 'Assigned',
    page: 1,
    limit: 10,
  });

  const { data, isLoading } = useSWR({ url: 'fleet/assignments', args: reqPayload }, fetcher)

  const rentalRequests: RentalRequest[] = useMemo(() => {
    return data?.data.map((item: any) => ({
      ...item.request,
      assignedBy: item.assignedBy,
      assignedValue: item.assignedValue,
      assignmentId: item.id
    })) || []
  }, [data])

  const items = [
    {name: 'Pending', value: 'PENDING'},
    {name: 'Active', value: 'ACTIVE'},
    {name: 'Completed', value: 'COMPLETED'},
  ]

  const handleRadioChange = useCallback((selected: string) => {
    setReqPayload({...reqPayload, status: selected})
  }, [reqPayload])

  const handleDataViewClick = useCallback((data: any) => {
    navigate(`/rentals/${data?.assignmentId}?categoryId=${data?.truckCategoryId}`);
  }, [navigate])

  return (
    <div>
      <div className="md:hidden text-center mt-4">
        <RadioGroup items={items} onChange={handleRadioChange} />
      </div>
      {/* Medium and Large Screen Tabs */}      
      <div className="hidden md:flex justify-between p-4">
        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="flex w-full justify-between  bg-transparent p-0 h-auto">
            <div className="w-1/2 flex">
            <TabsTrigger
              value="pending"
              className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-500 data-[state=active]:shadow-none rounded-none h-10 font-medium"
              onClick={() => setReqPayload({...reqPayload, status: 'Assigned'})}
            >
              Pending
            </TabsTrigger>
            <TabsTrigger
              value="active"
              className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-500 data-[state=active]:shadow-none rounded-none h-10 font-medium text-gray-500"
              onClick={() => setReqPayload({...reqPayload, status: 'Accepted'})}
            >
              Active
            </TabsTrigger>
            <TabsTrigger
              value="completed"
              className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-500 data-[state=active]:shadow-none rounded-none h-10 font-medium text-gray-500"
              onClick={() => setReqPayload({...reqPayload, status: 'Completed'})}
            >
              Completed
            </TabsTrigger>
            </div>
          <div className=" w-1/3 ">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                type="search"
                placeholder="Search"
                className="w-full pl-8 h-9 rounded-md border-gray-200"
              />
            </div>
          </div>
          </TabsList>
          <TabsContent value="pending" className="mt-0 p-0">
            <RequestTable data={rentalRequests} loading={isLoading} />
          </TabsContent>
          <TabsContent value="active" className="mt-0 p-0">
            <RequestTable data={rentalRequests} loading={isLoading} />
          </TabsContent>
          <TabsContent value="completed" className="mt-0 p-0">
            <RequestTable data={rentalRequests} loading={isLoading} />
          </TabsContent>
        </Tabs>
      </div>
      {/* Mobile Screen Cards */}
      <div className="md:hidden mt-4">
        <DataView
          loading={isLoading}
          records={rentalRequests}
          onClick={handleDataViewClick}
        />
      </div>
    </div>
  )
}

function RequestTable ({ data, loading }: { data: RentalRequest[], loading: boolean }) {
  const navigate = useNavigate();
  const [ formatCurrency ] = useCurrency();
  return (
    <table className="w-full">
      <thead>
        <tr className="border-b border-t">
          <th className="py-3 px-4 font-medium text-sm text-left">Reference</th>
          <th className="text-center py-3 px-4 font-medium text-sm">Vehicle</th>
          <th className="text-center py-3 px-4 font-medium text-sm">Value</th>
          <th className="text-center py-3 px-4 font-medium text-sm">Date</th>
          <th className="text-center py-3 px-4 font-medium text-sm">Loading Point</th>
          <th className="text-center py-3 px-4 font-medium text-sm">Trips</th>
          <th className="text-center py-3 px-4 font-medium text-sm">Assigned by</th>
        </tr>
      </thead>
      <tbody>
        {data.length > 0 && !loading && data.map((record, index) => (
          <tr key={index} className="border-b hover:bg-gray-100 hover:cursor-pointer" onClick={() => navigate(`/rentals/${record?.assignmentId}?categoryId=${record?.truckCategoryId}`)}>
            <td className="py-4 px-4">{record?.invoiceId}</td>
            <td className="py-4 px-4">
              <img
                src={record?.truckCategory?.image || placeholder}
                alt="Vehicle"
                className="w-8 h-8 border rounded p-1"
              />
            </td>
            <td className="py-4 px-4 text-orange-400">
              {formatCurrency(record?.assignedValue)}
            </td>
            <td className="py-4 px-4">{dayjs(record?.createdAt).format("DD/MM/YYYY")}</td>
            <td className="py-4 px-4">{record?.pickupAddress}</td>
            <td className="py-4 px-4">{record?.noOfTrips}</td>
            <td className="py-4 px-4">{record?.assignedBy?.name}</td>
          </tr>
        ))}
        {!data.length && !loading && (<tr>
              <td colSpan={7}>
                <div className="h-[400px] flex items-center justify-center text-gray-500">
                  No rental request found
                </div>
              </td>
            </tr>)
        }
        {loading && ((<tr>
            <td colSpan={7}>
              <div className="h-[400px] flex items-center justify-center text-gray-500">
                <Loader />
              </div>
            </td>
          </tr>)
        )}
      </tbody>
    </table>
  )
}
